/**
 * Hover magnifier for product galleries: while the pointer is over an image slide of a
 * `<media-gallery data-hover-zoom>`, the image scales up around the pointer position
 * (the CSS lives with the gallery and only applies to mouse devices).
 *
 * One delegated listener serves every gallery on the page; the slide gets a `data-zoomed`
 * attribute and `--hover-zoom-x/--hover-zoom-y` custom properties.
 */

const SLIDE_SELECTOR = '[data-hover-zoom] slideshow-slide.product-media-container--image';

/**
 * @param {Element} target
 * @returns {HTMLElement | null}
 */
function slideFrom(target) {
  const slide = target.closest(SLIDE_SELECTOR);
  return slide instanceof HTMLElement ? slide : null;
}

/** @param {PointerEvent} event */
function handleMove(event) {
  if (event.pointerType && event.pointerType !== 'mouse') return;
  const slide = event.target instanceof Element ? slideFrom(event.target) : null;
  if (!slide) return;

  const rect = slide.getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width) * 100;
  const y = ((event.clientY - rect.top) / rect.height) * 100;

  slide.style.setProperty('--hover-zoom-x', `${Math.min(100, Math.max(0, x)).toFixed(1)}%`);
  slide.style.setProperty('--hover-zoom-y', `${Math.min(100, Math.max(0, y)).toFixed(1)}%`);
  if (!slide.hasAttribute('data-zoomed')) slide.setAttribute('data-zoomed', '');
}

/** @param {PointerEvent} event */
function handleLeave(event) {
  if (!(event.target instanceof Element)) return;
  const slide = event.target.matches?.(SLIDE_SELECTOR) ? event.target : null;
  if (slide) slide.removeAttribute('data-zoomed');
}

// Reset when the gallery scrolls to another slide or a dialog opens over it
function resetAll() {
  document.querySelectorAll(`${SLIDE_SELECTOR}[data-zoomed]`).forEach((slide) => slide.removeAttribute('data-zoomed'));
}

document.addEventListener('pointermove', handleMove, { passive: true });
// pointerleave does not bubble: listen in the capture phase to delegate it
document.addEventListener('pointerleave', handleLeave, { passive: true, capture: true });
document.addEventListener('scroll', resetAll, { passive: true, capture: true });
